import { FC } from 'react'

import {
  Post,
  TextInsert,
  ImageInsert,
  JumpLink,
  PostResumeSample,
} from './post'
import { LandingPage } from './landingPage'
import {
  TextColumn,
  TextColumnGroup,
  StyledList,
  HoverText,
  TimeChip,
  AccordionList,
  TabbedComponentProps,
  ImageToggle,
} from './heroTextSection'
import { CTA } from './cta'
import { AssetFluid } from './asset'
import {
  ArticlesDisplayComponent,
  Button,
  NavigationGroup,
  PostCardsComponent,
  PriceComponent,
  ReferenceTextCopy,
  TrustPilotRatingWidget,
} from './page'
import { CustomComponent } from './landingPageV2'
import { VideoInsert } from './video'
import { MediaInsert } from './media'
import { InfoTable } from './table'
import { RangeSlider } from './sliders'
import { DynamicText } from './brand'
import { Carousel } from './carousel'
import { MatchTitlesAndDescriptions } from './match'
import { BlockOrInline } from 'components/RichText/ErrorBoundary'
import { IGenericGridReference } from './grid'
import { IResumeUpload } from './widget'

export enum ContentfulType {
  Post = 'ContentfulPost',
  PostResumeSample = 'ContentfulPostResumeSample',
  LandingPage = 'ContentfulLandingPage',
  TextInsert = 'ContentfulTextInsert',
  ImageInsert = 'ContentfulImageInsert',
  CallToAction = 'ContentfulCallToAction',
  Asset = 'ContentfulAsset',
  Button = 'ContentfulButton',
  JumpLink = 'ContentfulJumpLink',
  TextColumn = 'ContentfulTextColumn',
  TextColumnGroup = 'ContentfulTextColumnGroup',
  StyledList = 'ContentfulStyledList',
  CustomComponentType = 'ContentfulCustomComponent',
  HoverTextComponent = 'ContentfulHoverTextComponent',
  TimeChipComponent = 'ContentfulTimeChip',
  AccordionListComponent = 'ContentfulAccordionList',
  ImageToggleComponent = 'ContentfulImageToggle',
  TabbedComponent = 'ContentfulTabbedComponent',
  VideoInsertComponent = 'ContentfulVideoInsert',
  MediaInsertComponent = 'ContentfulMediaInsert',
  InfoTableComponent = 'ContentfulInfoTable',
  RangeSliderComponent = 'ContentfulRangeSlider',
  NavigationGroupComponent = 'ContentfulNavigationGroup',
  PriceComponent = 'ContentfulPriceComponent',
  PostCardsComponent = 'ContentfulPostCards',
  DynamicTextComponent = 'ContentfulDynamicText',
  ReferencedTextCopy = 'ContentfulReferencedTextCopy',
  TrustPilotRatingWidget = 'ContentfulTrustPilotRatingWidget',
  ArticlesDisplay = 'ContentfulArticlesDisplay',
  CarouselComponent = 'ContentfulCarousel',
  MatchTitlesAndDesc = 'ContentfulMatchTitlesAndDescriptions',
  GridComponent = 'ContentfulGrid',
  ResumeUpload = 'ContentfulResumeUpload',
}

export interface BaseReference<T> {
  contentful_id: string
  __typename: T
}

export type PostReference = Pick<Post, 'slug' | 'title'> &
  BaseReference<ContentfulType.Post>
export type PostResumeSampleReference = Pick<
  PostResumeSample,
  'slug' | 'title'
> &
  BaseReference<ContentfulType.PostResumeSample>
export type LandingPageReference = Pick<LandingPage, 'slug' | 'title'> &
  BaseReference<ContentfulType.LandingPage>
export type TextInsertReference = Pick<
  TextInsert,
  'textType' | 'text' | 'source'
> &
  BaseReference<ContentfulType.TextInsert>
export type ImageInsertReference = ImageInsert
export type CallToActionReference = CTA &
  BaseReference<ContentfulType.CallToAction>
export type AssetReference = AssetFluid & BaseReference<ContentfulType.Asset>
export type ButtonReference = Button
export type JumpLinkReference = JumpLink
export type TextColumnReference = TextColumn
export type TextColumnGroupReference = TextColumnGroup
export type StyledListReference = StyledList
export type CustomComponentReference = CustomComponent
export type HoverTextComponentReference = HoverText
export type TimeChipReference = TimeChip
export type AccordionListComponentReference = AccordionList
export type ImageToggleReference = ImageToggle
export type TabbedComponentReference = TabbedComponentProps
export type VideoInsertReference = VideoInsert
export type MediaInsertReference = MediaInsert
export type InfoTableReference = InfoTable
export type RangeSliderReference = RangeSlider
export type NavigationGroupReference = NavigationGroup
export type PriceComponentReference = PriceComponent
export type PostCardComponentReference = PostCardsComponent
export type DynamicTextReference = DynamicText
export type ReferenceTextCopyReference = ReferenceTextCopy
export type TrustPilotRatingWidgetReference = TrustPilotRatingWidget
export type ArticlesDisplayReference = ArticlesDisplayComponent
export type CarouselReference = Carousel
export type MatchReference = MatchTitlesAndDescriptions

export interface CustomElementProps<RefType> {
  node: BlockOrInline
  children: React.ReactNode
  reference: RefType
}

export type ReferenceTypeMap = {
  [ContentfulType.TextInsert]: TextInsertReference
  [ContentfulType.Post]: PostReference
  [ContentfulType.PostResumeSample]: PostResumeSampleReference
  [ContentfulType.LandingPage]: LandingPageReference
  [ContentfulType.ImageInsert]: ImageInsertReference
  [ContentfulType.CallToAction]: CallToActionReference
  [ContentfulType.Asset]: AssetReference
  [ContentfulType.Button]: ButtonReference
  [ContentfulType.JumpLink]: JumpLinkReference
  [ContentfulType.TextColumn]: TextColumnReference
  [ContentfulType.TextColumnGroup]: TextColumnGroupReference
  [ContentfulType.StyledList]: StyledListReference
  [ContentfulType.CustomComponentType]: CustomComponentReference
  [ContentfulType.HoverTextComponent]: HoverTextComponentReference
  [ContentfulType.TimeChipComponent]: TimeChip
  [ContentfulType.AccordionListComponent]: AccordionListComponentReference
  [ContentfulType.TabbedComponent]: TabbedComponentReference
  [ContentfulType.ImageToggleComponent]: ImageToggleReference
  [ContentfulType.VideoInsertComponent]: VideoInsertReference
  [ContentfulType.MediaInsertComponent]: MediaInsertReference
  [ContentfulType.InfoTableComponent]: InfoTableReference
  [ContentfulType.RangeSliderComponent]: RangeSliderReference
  [ContentfulType.NavigationGroupComponent]: NavigationGroupReference
  [ContentfulType.PriceComponent]: PriceComponentReference
  [ContentfulType.PostCardsComponent]: PostCardComponentReference
  [ContentfulType.DynamicTextComponent]: DynamicTextReference
  [ContentfulType.ReferencedTextCopy]: ReferenceTextCopyReference
  [ContentfulType.TrustPilotRatingWidget]: TrustPilotRatingWidgetReference
  [ContentfulType.ArticlesDisplay]: ArticlesDisplayReference
  [ContentfulType.CarouselComponent]: CarouselReference
  [ContentfulType.MatchTitlesAndDesc]: MatchReference
  [ContentfulType.GridComponent]: IGenericGridReference
  [ContentfulType.ResumeUpload]: IResumeUpload
}

export type CustomElementMap = {
  [K in ContentfulType]: FC<CustomElementProps<ReferenceTypeMap[K]>>
}

export type ReferenceType =
  | AssetReference
  | ButtonReference
  | CallToActionReference
  | CustomComponentReference
  | HoverTextComponentReference
  | ImageInsertReference
  | JumpLinkReference
  | LandingPageReference
  | PostReference
  | PostResumeSampleReference
  | StyledListReference
  | TextColumnGroupReference
  | TextColumnReference
  | TextInsertReference
  | TimeChipReference
  | ImageToggleReference
  | TabbedComponentReference
  | VideoInsertReference
  | MediaInsertReference
  | InfoTableReference
  | RangeSliderReference
  | NavigationGroupReference
  | PriceComponentReference
  | PostCardComponentReference
  | DynamicTextReference
  | ReferenceTextCopyReference
  | TrustPilotRatingWidgetReference
  | ArticlesDisplayReference
  | CarouselReference
  | MatchReference
  | IResumeUpload
  | IGenericGridReference
